import React from 'react';
import styled from 'styled-components';
import { colors, Regular } from '../../core/Styles';

export const List = styled.div`
  margin-top: 16px;
  margin-left: -23px;
  margin-right: -16px;
`;

export const ListHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.faintGrey};
  margin-left: 23px;
  margin-right: 16px;
  & > :last-child {
    padding-right: 14px;
  }
`;

export const ListHeaderItem = styled.div<{ span: number }>`
  flex: ${props => props.span};
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.grey};
  padding: 12px 0;
`;

export const ListBody = styled.div`
  overflow-y: auto;
  max-height: 65vh;
`;

export const ListItem = styled.div`
  display: flex;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: ${colors.normal};
  padding: 12px 16px 12px 23px;
  & > :last-child {
    padding-right: 14px;
  }
  &:hover {
    background-color: ${colors.background};
    transition: background-color 0.2s ease;
  }
  & > :not(:last-child) {
    margin-right: 16px;
  }
`;

export const ListItemColumn = styled.div<{ span: number }>`
  display: flex;
  flex: ${props => props.span};
  align-items: center;
  & > :not(:last-child) {
    margin-right: 12px;
  }
  & > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  min-width: 0;
`;

export const ListItemHorizontalDivider = styled.div`
  height: 1px;
  background-color: ${colors.faintGrey};
  margin-left: 23px;
  margin-right: 16px;
`;

export const EmptyStateDiv = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px;
`;

export const StatusBadge = styled.span<{ status?: string }>`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => {
    const status = props.status?.toLowerCase() || 'pending';
    // Accepted - Green
    if (status === 'accepted') {
      return '#10b981';
    }
    // Pending - Yellow
    if (status === 'pending') {
      return '#f59e0b';
    }
    // Denied or Deleted - Red
    if (status === 'denied' || status === 'deleted') {
      return '#ef4444';
    }
    return '#f59e0b'; // Default to Pending (yellow)
  }};
  color: #ffffff;
  text-transform: capitalize;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
`;

export const RequestTypeBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${colors.background};
  color: ${colors.primary};
  text-transform: capitalize;
  border: 1px solid ${colors.faintGrey};
`;

export const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const ActionButton = styled.button<{ variant: 'accept' | 'deny' }>`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 16px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid;

  ${props => {
    if (props.variant === 'accept') {
      return `
        background-color: #10b981;
        color: #ffffff;
        border-color: #10b981;
        &:hover {
          background-color: #059669;
          border-color: #059669;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
        }
        &:active {
          transform: translateY(0);
        }
      `;
    } else {
      return `
        background-color: #ef4444;
        color: #ffffff;
        border-color: #ef4444;
        &:hover {
          background-color: #dc2626;
          border-color: #dc2626;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(239, 68, 68, 0.3);
        }
        &:active {
          transform: translateY(0);
        }
      `;
    }
  }}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 24px 0;
  margin-top: 24px;
  border-top: 2px solid ${colors.faintGrey};
  flex-wrap: wrap;
  background-color: ${colors.background};
  border-radius: 0 0 8px 8px;
`;

export const PaginationButton = styled.button<{ disabled?: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  color: ${props => (props.disabled ? colors.grey : colors.primary)};
  background-color: ${props => (props.disabled ? colors.faintGrey : '#ffffff')};
  border: 1px solid ${props => (props.disabled ? colors.faintGrey : colors.faintGrey)};
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &:hover:not(:disabled) {
    background-color: ${colors.primary};
    color: #ffffff;
    border-color: ${colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PageNumbers = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const PageNumberButton = styled.button<{ active?: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: ${props => (props.active ? '700' : '600')};
  font-size: 14px;
  color: ${props => (props.active ? '#ffffff' : colors.normal)};
  background-color: ${props => (props.active ? colors.primary : '#ffffff')};
  border: 1px solid ${props => (props.active ? colors.primary : colors.faintGrey)};
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  &:hover {
    background-color: ${props => (props.active ? colors.primary : colors.primary)};
    color: #ffffff;
    border-color: ${colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

export const Ellipsis = styled.span`
  padding: 0 4px;
  color: ${colors.grey};
  font-weight: 600;
`;

export const PageInfo = styled(Regular)`
  color: ${colors.grey};
  font-size: 14px;
  margin: 0 8px;
`;

export const StatusFilterWrapper = styled.div`
  min-width: 140px;
`;

export const RequestTypeFilterWrapper = styled.div`
  min-width: 180px;
`;
